import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { DATADOG_SERVICE, DATADOG_SITE, DEVELOP, ENV_PREPROD, ENV_PROD, PACKAGE_VERSION } from '../constants/datadog'
import { UserInfo } from './useAuthUserInfo'
import { parseBoolean } from '../utils/boolean'

interface DatadogConfig {
  REACT_APP_ENV: string
  REACT_APP_DATADOG_APP_ID: string
  REACT_APP_DATADOG_CLIENT_TOKEN: string
  REACT_APP_DATADOG_SESSION_SAMPLE_RATE: number
  REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE: number
  REACT_APP_DATADOG_TRACK_USER_INTERACTIONS: boolean
}

const getDatadogConfig = (): DatadogConfig => {
  const env = import.meta.env
  return {
    REACT_APP_ENV: env.REACT_APP_ENV,
    REACT_APP_DATADOG_APP_ID: env.REACT_APP_DATADOG_APP_ID,
    REACT_APP_DATADOG_CLIENT_TOKEN: env.REACT_APP_DATADOG_CLIENT_TOKEN,
    REACT_APP_DATADOG_SESSION_SAMPLE_RATE: Number.parseInt(env.REACT_APP_DATADOG_SESSION_SAMPLE_RATE, 10),
    REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE: Number.parseInt(env.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE, 10),
    REACT_APP_DATADOG_TRACK_USER_INTERACTIONS: parseBoolean(env.REACT_APP_DATADOG_TRACK_USER_INTERACTIONS)
  }
}

export const useDatadog = (user: UserInfo) => {
  useEffect(() => {
    const shouldInitializeDatadog = [ENV_PROD, ENV_PREPROD, DEVELOP].includes(import.meta.env.REACT_APP_ENV)
    if (user && shouldInitializeDatadog) {
      const config = getDatadogConfig()

      datadogRum.init({
        applicationId: config.REACT_APP_DATADOG_APP_ID,
        clientToken: config.REACT_APP_DATADOG_CLIENT_TOKEN,
        site: DATADOG_SITE,
        service: DATADOG_SERVICE,
        version: PACKAGE_VERSION,
        env: config.REACT_APP_ENV,
        sessionSampleRate: config.REACT_APP_DATADOG_SESSION_SAMPLE_RATE,
        sessionReplaySampleRate: config.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
        trackUserInteractions: config.REACT_APP_DATADOG_TRACK_USER_INTERACTIONS,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend: event => {
          // Browsers silently ignore preventDefault() on touchmove / touchend events when scrolling is in progress (passive listeners).
          // This is expected behavior and has no user impact — filter it to avoid noise in Datadog.
          if (event.type === 'error' && event.error.message.includes('cancelable=false')) {
            return false
          }
        }
      })
      datadogRum.startSessionReplayRecording()

      datadogLogs.init({
        clientToken: config.REACT_APP_DATADOG_CLIENT_TOKEN,
        site: DATADOG_SITE,
        service: DATADOG_SERVICE,
        env: config.REACT_APP_ENV,
        version: PACKAGE_VERSION,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100
      })
      datadogRum.setUser({ id: user.uid })
      datadogRum.setUserProperty('sitename', user.sitename ?? 'not-available')
      datadogRum.setUserProperty('job_name', user.jobname ?? 'not-available')
      datadogLogs.setUser({ id: user.uid, job_name: user.jobname ?? 'not-available' })
    }
  }, [user])
}
